import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Search, Star, MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import HomeBanner from "../components/HomeBanner";

const SPORTS = ["All", "Football", "Box Cricket", "Badminton", "Basketball", "Pickleball", "Padel"];

export default function Home() {
    const [centres, setCentres] = useState([]);
    const [sport, setSport] = useState("All");
    const [city, setCity] = useState("All");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    const fetchCentres = async () => {
        setLoading(true);
        try {
            const params = {};
            if (sport !== "All") params.sport = sport;
            if (q) params.q = q;
            const { data } = await api.get("/centres", { params });
            setCentres(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCentres(); /* eslint-disable-next-line */ }, [sport]);

    const cities = useMemo(() => {
        const set = new Set(centres.map((c) => c.city).filter(Boolean));
        return ["All", ...Array.from(set).sort()];
    }, [centres]);

    const filteredCentres = useMemo(() => {
        if (city === "All") return centres;
        return centres.filter((c) => c.city === city);
    }, [centres, city]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
            {/* Live announcement banner (admin-managed) */}
            <HomeBanner />

            {/* Hero */}
            <section
                className="relative rounded-lg overflow-hidden mb-8 border border-slate-800"
                style={{ minHeight: 220 }}
                data-testid="home-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/9ec97b8b-c102-455e-b976-d7c2eab1132e/images/f2d82c4852b71397ad0f562b781e8bd61081b20444f9a408f388cfdb276d83c3.png)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/30" />
                <div className="relative z-10 p-6 sm:p-10 max-w-2xl">
                    <div className="text-[10px] font-bold tracking-[0.4em] text-[#00FF66] uppercase">Find your turf</div>
                    <h1 className="font-display text-3xl sm:text-5xl font-bold uppercase tracking-tight text-white mt-2 leading-none">
                        Book the field.<br /><span className="text-[#00FF66]">Make it count.</span>
                    </h1>
                    <p className="text-slate-300 mt-3 text-sm sm:text-base max-w-md">
                        Multi-sport turf bookings — Football, Cricket, Padel, Badminton & more.
                    </p>
                </div>
            </section>

            {/* Search */}
            <div className="relative mb-5">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchCentres()}
                    placeholder="Search centres, areas, cities..."
                    className="w-full h-12 pl-11 pr-4 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#00FF66]"
                    data-testid="home-search-input"
                />
            </div>

            {/* Sport filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
                {SPORTS.map((s) => (
                    <button
                        key={s}
                        onClick={() => setSport(s)}
                        data-testid={`sport-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-colors border ${
                            sport === s
                                ? "bg-[#00FF66] text-black border-[#00FF66]"
                                : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            {/* City filter */}
            <div className="flex items-center gap-2 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto pb-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500 whitespace-nowrap">
                    <MapPin className="w-3 h-3" /> Location
                </span>
                {cities.map((cty) => (
                    <button
                        key={cty}
                        onClick={() => setCity(cty)}
                        data-testid={`city-filter-${cty.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`px-3 h-7 rounded-full text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap transition-colors border ${
                            city === cty
                                ? "bg-slate-800 text-[#00FF66] border-[#00FF66]/50"
                                : "bg-slate-900/50 text-slate-400 border-slate-800 hover:border-slate-600"
                        }`}
                    >
                        {cty}
                    </button>
                ))}
            </div>

            {/* Centres */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold uppercase text-white">Centres near you</h2>
                <span className="text-xs text-slate-400">{filteredCentres.length} found</span>
            </div>

            {loading ? (
                <div className="text-center py-16 text-slate-500 uppercase tracking-widest text-sm">Loading centres...</div>
            ) : filteredCentres.length === 0 ? (
                <div className="text-center py-16 text-slate-500">No centres match your filter.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredCentres.map((c) => (
                        <Link
                            key={c.id}
                            to={`/centres/${c.id}`}
                            data-testid={`centre-card-${c.id}`}
                            className="group block bg-slate-900 border border-slate-800 rounded-lg overflow-hidden card-hover"
                        >
                            <div className="relative h-44 overflow-hidden">
                                <img src={c.photo_url} alt={c.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
                                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 h-7 rounded-full bg-slate-950/85 border border-slate-700 text-xs font-bold text-white">
                                    <Star className="w-3 h-3 fill-[#00FF66] text-[#00FF66]" />
                                    {c.rating}
                                </div>
                            </div>
                            <div className="p-4">
                                <h3 className="font-display font-bold text-lg uppercase tracking-tight text-white">{c.name}</h3>
                                <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                                    <MapPin className="w-3 h-3" />
                                    {c.address}
                                </div>
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {c.sports.slice(0, 4).map((s) => (
                                        <span key={s} className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">
                                            {s}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-3">
                                    <span className="text-xs text-slate-500">{c.distance_km} km away</span>
                                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                                        Book <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Parent brand strip — moved to bottom & compacted (Iter 26b) */}
            <div className="mt-10 mb-2 flex justify-center" data-testid="all-sports-brand-footer">
                <Link
                    to="/about"
                    data-testid="all-sports-brand-pill"
                    className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00FF66]/30 bg-[#00FF66]/5 hover:border-[#00FF66]/60 hover:bg-[#00FF66]/10 transition-colors"
                >
                    <ShieldCheck className="w-3 h-3 text-[#00FF66]" />
                    <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#00FF66]">An ALL SPORTS Brand</span>
                    <ArrowRight className="w-3 h-3 text-slate-500 group-hover:text-[#00FF66]" />
                </Link>
            </div>
        </div>
    );
}
